import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../contexts/ThemeContext";
import { NavbarContext } from "../contexts/NavbarContext";
import { useTranslation } from "react-i18next";
import { ABOUT, BLOG, CONTACT, HELP, HOME, PROFILE, RDV, REALISATIONS, SERVICES } from "../../config/routes";

import "./styles/Navbar.css";

import logoPng from "../../images/png/logo/dreamer_agency_logo.png";
import helpIcon from "../../images/icons/help.svg";
import helpDarkIcon from "../../images/icons/help_dark.svg";
import lightIcon from "../../images/icons/light.svg";
import darkIcon from "../../images/icons/dark.svg";
import languageIcon from "../../images/icons/language.svg";
import languageDarkIcon from "../../images/icons/language_dark.svg";
import frenchFlagIcon from "../../images/icons/french_flag.svg";
import englishFlagIcon from "../../images/icons/english_flag.svg";
import hamburgerMenu from "../../images/icons/hamburger_menu.svg";

import logoAvif from "../../images/avif/logo/dreamer_agency_logo.avif";
import logoWebp from "../../images/webp/logo/dreamer_agency_logo.webp";

const Navbar = () => {
  const { darkMode, setDarkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, setScrollPosition } = useContext(NavbarContext);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false);
  const menuLanguageRef = useRef(null);
  const iconLanguageRef = useRef(null);

  const menuMobileRef = useRef(null);
  const menuMobileIconRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleThemeToggle = () => setDarkMode((v) => !v);

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    setLanguageMenuOpen(false);
  };

  const handleCloseLanguageMenu = (e) => {
    if (
      menuLanguageRef.current &&
      !menuLanguageRef.current.contains(e.target) &&
      !iconLanguageRef.current?.contains(e.target)
    ) {
      setLanguageMenuOpen(false);
    }
  };

  const handleNavbarToggle = () => {
    if (!navbarActive) {
      const currentScroll = window.scrollY || document.documentElement.scrollTop;
      setScrollPosition(currentScroll);
    }
    setNavbarActive(!navbarActive);
  };

  const resetNavbar = () => {
    setNavbarActive(false);
    setScrollPosition(0);
  };

  const handleClickOutside = useCallback(
    (e) => {
      if (
        menuMobileRef.current &&
        !menuMobileRef.current.contains(e.target) &&
        !menuMobileIconRef.current.contains(e.target)
      ) {
        if (navbarActive) setNavbarActive(false);
      }
    },
    [navbarActive, setNavbarActive]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled((window.scrollY || 0) > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isLanguageMenuOpen) document.addEventListener("click", handleCloseLanguageMenu);
    return () => document.removeEventListener("click", handleCloseLanguageMenu);
  }, [isLanguageMenuOpen]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    const preventScroll = (e) => e.preventDefault();
    if (navbarActive) document.addEventListener("touchmove", preventScroll, { passive: false });
    else document.removeEventListener("touchmove", preventScroll);
    return () => document.removeEventListener("touchmove", preventScroll);
  }, [navbarActive]);

  useEffect(() => {
    if (navbarActive) setNavbarActive(false);
  }, [location.pathname]);

  const headerClass = `navbar ${darkMode ? "navbar--dark" : "navbar--light"} ${
    isScrolled ? "navbar--scrolled" : ""
  }`;

  return (
    <header className={headerClass}>
      {!isMobile ? (
        <div className="navbar-desktop">
          <div className="navbar-inner">
            <Link to={HOME} className="navbar-logo" aria-label="Dreamer Agency">
              <picture>
                <source srcSet={logoAvif} type="image/avif" />
                <source srcSet={logoWebp} type="image/webp" />
                <img src={logoPng} alt="Logo Dreamer Agency" loading="eager" />
              </picture>
            </Link>

            <nav className="navbar-links" aria-label="Navigation principale">
              <Link to={HOME} className="navbar-link">
                {t("navbar.home")}
              </Link>
              <Link to={SERVICES} className="navbar-link">
                {t("navbar.offers")}
              </Link>
              <Link to={ABOUT} className="navbar-link">
                {t("navbar.about")}
              </Link>
              <Link to={REALISATIONS} className="navbar-link">
                {t("navbar.achievements")}
              </Link>
              <Link to={PROFILE} className="navbar-link">
                Profil développeur
              </Link>
              <Link to={BLOG} className="navbar-link">
                {t("navbar.blog")}
              </Link>
              <Link to={CONTACT} className="navbar-link">
                {t("navbar.contact")}
              </Link>
            </nav>

            <div className="navbar-actions">
              <Link to={RDV} className="navbar-cta">
                Réserver un appel
              </Link>

              <Link to={HELP} className="navbar-iconBtn" aria-label="Aide">
                <img src={darkMode ? helpIcon : helpDarkIcon} alt="" />
              </Link>

              <div className="navbar-language">
                <button
                  ref={iconLanguageRef}
                  className="navbar-iconBtn"
                  onClick={() => setLanguageMenuOpen((v) => !v)}
                  aria-label="Changer la langue"
                >
                  <img src={darkMode ? languageIcon : languageDarkIcon} alt="" />
                </button>

                {isLanguageMenuOpen && (
                  <div ref={menuLanguageRef} className="navbar-langDropdown" role="menu">
                    <button className="navbar-langOption" onClick={() => changeLanguage("fr")}>
                      <img src={frenchFlagIcon} alt="" /> Français
                    </button>
                    <button className="navbar-langOption" onClick={() => changeLanguage("en")}>
                      <img src={englishFlagIcon} alt="" /> English
                    </button>
                  </div>
                )}
              </div>

              <button className="navbar-iconBtn" onClick={handleThemeToggle} aria-label="Thème">
                <img src={darkMode ? lightIcon : darkIcon} alt="" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="navbar-mobile">
          <div className="navbar-mobileBar">
            <Link to={HOME} className="navbar-mobileLogo" onClick={resetNavbar} aria-label="Dreamer Agency">
              <picture>
                <source srcSet={logoAvif} type="image/avif" />
                <source srcSet={logoWebp} type="image/webp" />
                <img src={logoPng} alt="Logo Dreamer Agency" loading="eager" />
              </picture>
            </Link>

            <button
              className="navbar-mobileMenuBtn"
              onClick={handleNavbarToggle}
              ref={menuMobileIconRef}
              aria-label="Ouvrir le menu"
            >
              <img src={hamburgerMenu} alt="" />
            </button>
          </div>

          <nav
            aria-label="Menu mobile"
            className={`navbar-sidebar ${navbarActive ? "active" : ""} ${darkMode ? "dark" : "light"}`}
            ref={menuMobileRef}
          >
            <Link to={RDV} className="navbar-sidebar-cta" onClick={resetNavbar}>
              Réserver un appel
            </Link>

            <Link to={HOME} className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.home")}
            </Link>
            <Link to={SERVICES} className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.offers")}
            </Link>
            <Link to={ABOUT} className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.about")}
            </Link>
            <Link to={REALISATIONS} className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.achievements")}
            </Link>
            <Link to={PROFILE} className="navbar-sidebar-link" onClick={resetNavbar}>
              Profil développeur
            </Link>
            <Link to={BLOG} className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.blog")}
            </Link>
            <Link to={CONTACT} className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.contact")}
            </Link>
            <Link to={HELP} className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.help")}
            </Link>

            <div className="navbar-mobileRow">
              <button className="navbar-miniBtn" onClick={() => changeLanguage("fr")}>
                FR
              </button>
              <button className="navbar-miniBtn" onClick={() => changeLanguage("en")}>
                EN
              </button>
              <button className="navbar-miniBtn" onClick={handleThemeToggle}>
                {darkMode ? "Light" : "Dark"}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
